import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { useAuth } from '../auth/auth-store';
import { MemberSelect } from '../ui/UserSelect';
import { listCatalogue } from '../settings/catalogues-api';
import { createLead } from './leads-api';

/**
 * A new lead, in as few fields as the job allows.
 *
 * One required field: the name. Everything else is optional, because the moment a lead is worth
 * recording is usually the moment somebody has half the information — a name and a phone number
 * scribbled during a call — and a form that demands an e-mail address at that moment is a form
 * people work around by not using it.
 *
 * `<dialog>` rather than a div, so Escape, focus trapping and the backdrop come from the browser.
 * Saving goes straight to the record, because the next thing anybody does with a new lead is work
 * it.
 */
export function NewLeadDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): React.JSX.Element | null {
  const dialog = useRef<HTMLDialogElement>(null);
  const navigate = useNavigate();
  const client = useQueryClient();
  const { user } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const sources = useQuery({
    queryKey: ['catalogue', 'sources'],
    queryFn: () => listCatalogue('customer-sources'),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  useEffect(() => {
    const element = dialog.current;

    if (!element) return;

    if (open && !element.open) {
      setName('');
      setEmail('');
      setPhone('');
      setCompany('');
      setSourceId('');
      // A lead lands on whoever created it unless they say otherwise: an unowned lead is a lead
      // nobody answers.
      setOwnerId(user?.id ?? '');
      setError(null);
      element.showModal();
    }

    if (!open && element.open) element.close();
  }, [open, user?.id]);

  const create = useMutation({
    mutationFn: () =>
      createLead({
        name: name.trim(),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(company.trim() ? { companyName: company.trim() } : {}),
        ...(sourceId ? { sourceId } : {}),
        ...(ownerId ? { ownerId } : {}),
      }),
    onSuccess: (lead) => {
      void client.invalidateQueries({ queryKey: ['leads'] });
      void client.invalidateQueries({ queryKey: ['lead-summary'] });
      onClose();
      navigate(`/crm/registro/leads/${lead.id}`);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (!open) return null;

  return (
    <dialog ref={dialog} className="confirm confirm--wide" onCancel={onClose} onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <h2>{t('crm.newLead')}</h2>
        <p className="muted">{t('crm.newLeadHint')}</p>

        <label className="stack__field">
          <span>
            {t('crm.field.name')} <span className="required">*</span>
          </span>
          <input
            value={name}
            placeholder={t('crm.newLeadNamePlaceholder')}
            maxLength={200}
            autoFocus
            onChange={(event) => setName(event.target.value)}
          />
        </label>

        <div className="form-grid">
          <label className="stack__field">
            <span>{t('crm.field.email')}</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              maxLength={254}
            />
          </label>

          <label className="stack__field">
            <span>{t('crm.field.phone')}</span>
            <input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              maxLength={40}
            />
          </label>
        </div>

        <label className="stack__field">
          <span>{t('crm.field.company')}</span>
          <input
            value={company}
            onChange={(event) => setCompany(event.target.value)}
            maxLength={200}
          />
        </label>

        <div className="form-grid">
          <label className="stack__field">
            <span>{t('crm.field.source')}</span>
            <select
              className="select"
              value={sourceId}
              onChange={(event) => setSourceId(event.target.value)}
            >
              <option value="">{t('crm.noSource')}</option>
              {(sources.data ?? []).map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
          </label>

          <div className="stack__field">
            <span>{t('crm.owner')}</span>
            <MemberSelect
              value={ownerId || null}
              placeholder={t('crm.unassigned')}
              onChange={(next) => setOwnerId(next ?? '')}
            />
          </div>
        </div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="confirm__actions">
          <button type="button" className="button button--quiet button--compact" onClick={onClose}>
            {t('confirm.cancel')}
          </button>

          <button
            type="submit"
            className="button button--primary button--compact"
            disabled={name.trim() === '' || create.isPending}
          >
            {create.isPending ? t('work.creating') : t('crm.saveLead')}
          </button>
        </div>
      </form>
    </dialog>
  );
}
